/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, action } from 'mobx';
import { observer, inject } from 'mobx-react';

import { filterNames } from '../../../models/environments-sc/ScEnvironmentsStore';
import { filterColorMap, getFilterColor } from '../../../models/environments-sc/ScEnvironmentsFilterColors';

// Status-to-filter mapping (mirrors statusFilterMethod in ScEnvironmentsList)
const statusToFilter = {
  [filterNames.ACTIVE]: ['COMPLETED', 'COMPLETED_EXPIRED', 'TAINTED', 'STOPPED', 'PENDING', 'EXPIRED', 'INSUFFICIENT_FUNDS', 'TERMINATING', 'STARTING', 'STOPPING', 'RESTARTING', 'MODIFYING', 'MODIFYING_FAILED', 'FAILED', 'TERMINATING_FAILED', 'STARTING_FAILED', 'STOPPING_FAILED'],
  [filterNames.RUNNING]: ['COMPLETED', 'COMPLETED_EXPIRED', 'TAINTED'],
  [filterNames.STOPPED]: ['STOPPED', 'EXPIRED', 'INSUFFICIENT_FUNDS'],
  [filterNames.PENDING]: ['PENDING', 'STARTING', 'STOPPING', 'RESTARTING', 'MODIFYING'],
  [filterNames.ERRORED]: ['FAILED', 'TERMINATING_FAILED', 'STARTING_FAILED', 'STOPPING_FAILED', 'MODIFYING_FAILED', 'EXPIRED', 'COMPLETED_EXPIRED', 'INSUFFICIENT_FUNDS'],
  [filterNames.DELETED]: ['TERMINATED', 'TERMINATING'],
};

// expected props
// - selectedFilter (via prop) the filter name of the currently selected filter
// - onSelectedFilter (via prop) a fn to be called when a button is selected
// - filteredCount (via prop) optional — number of results after search filtering for the selected filter
// - scEnvironmentsStore (via injection)
class ScEnvironmentsFilterButtons extends React.Component {
  get selectedFilter() {
    return this.props.selectedFilter;
  }

  get onSelectedFilter() {
    const fn = this.props.onSelectedFilter;
    return _.isFunction(fn) ? fn : () => {};
  }

  handleSelected = name =>
    action(() => {
      this.onSelectedFilter(name);
    });

  getCounts() {
    const store = this.props.scEnvironmentsStore;
    if (!store) return {};

    const envs = store.list || [];
    const counts = {};
    _.forEach(_.keys(statusToFilter), filterName => {
      const statuses = statusToFilter[filterName];
      counts[filterName] = _.filter(envs, env => _.includes(statuses, env.status)).length;
    });
    return counts;
  }

  render() {
    const selectedFilter = this.selectedFilter;
    const counts = this.getCounts();
    const filteredCount = this.props.filteredCount;
    const isAdmin = this.props.isAdmin;

    const getButtonStyle = (name) => {
      const colors = getFilterColor(name);
      const isSelected = name === selectedFilter;
      
      return {
        backgroundColor: isSelected ? colors.activeBg : colors.bg,
        color: isSelected ? '#ffffff' : colors.color,
      };
    };

    // Hide "Needs Attention" filter for non-admins
    const visibleFilters = _.keys(filterColorMap).filter(name => {
      if (name === filterNames.ERRORED && !isAdmin) return false;
      return true;
    });

    return (
      <div className="workspace-filter-container">
        {_.map(visibleFilters, name => {
          const isSelected = name === selectedFilter;
          const totalForFilter = counts[name] || 0;

          // For selected filter: show filteredCount/total if search is active, otherwise just total
          // For unselected: show total count
          let countLabel;
          if (isSelected && filteredCount !== undefined && filteredCount !== totalForFilter) {
            countLabel = `${filteredCount}/${totalForFilter}`;
          } else {
            countLabel = `${totalForFilter}`;
          }

          return (
            <button
              key={name}
              className={`workspace-filter-btn ${isSelected ? 'selected' : ''}`}
              style={getButtonStyle(name)}
              onClick={!isSelected ? this.handleSelected(name) : undefined}
            >
              {_.startCase(name)}
              <span style={{
                marginLeft: '6px',
                fontSize: '0.85em',
                opacity: isSelected ? 0.9 : 0.7,
              }}>
                {countLabel}
              </span>
            </button>
          );
        })}
      </div>
    );
  }
}

decorate(ScEnvironmentsFilterButtons, {
  selectedFilter: computed,
  onSelectedFilter: computed,
});

export default inject('scEnvironmentsStore')(observer(ScEnvironmentsFilterButtons));
